import { useId, useState } from "react";
import type { FormEvent, ReactNode } from "react";
import type { Translate } from "./types.js";

/** Same minimum as the server (`PASSWORD_MIN_LENGTH`); the server stays the authority. */
export const MIN_PASSWORD_LENGTH = 12;

/**
 * Change-password form. `forced` is the first-sign-in screen (the password was set by an Owner or
 * the CLI): it explains why and offers sign-out instead of cancel. The new password must differ
 * from the current one; the server enforces that too.
 */
export function ChangePasswordForm(props: {
  t: Translate;
  forced?: boolean;
  /** Throw to show an error. A `code` of `unauthenticated` is shown as a wrong current password. */
  submit: (currentPassword: string, newPassword: string) => Promise<void>;
  onDone: () => void;
  errorText: (err: unknown) => string;
  onSignOut?: () => void;
}): ReactNode {
  const { t } = props;
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [repeat, setRepeat] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ids = [useId(), useId(), useId()] as const;

  const check = (): string | null => {
    if (next.length < MIN_PASSWORD_LENGTH)
      return t("ui.password.tooShort", { min: MIN_PASSWORD_LENGTH });
    if (next !== repeat) return t("ui.password.mismatch");
    if (next === current) return t("ui.password.same");
    return null;
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const problem = check();
    if (problem) {
      setError(problem);
      return;
    }
    setBusy(true);
    setError(null);
    props
      .submit(current, next)
      .then(
        () => {
          setCurrent("");
          setNext("");
          setRepeat("");
          props.onDone();
        },
        (err: unknown) => {
          const code = (err as { code?: string } | null)?.code;
          setError(
            code === "unauthenticated" ? t("ui.password.wrongCurrent") : props.errorText(err),
          );
        },
      )
      .finally(() => setBusy(false));
  };

  return (
    <form onSubmit={onSubmit} className="stack" aria-label={t("ui.password.title")}>
      <p className="muted">
        {props.forced ? t("ui.password.forcedIntro") : t("ui.password.intro")}
      </p>
      <div className="field">
        <label htmlFor={ids[0]}>{t("ui.password.current")}</label>
        <input
          id={ids[0]}
          type="password"
          autoComplete="current-password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          required
          autoFocus
        />
      </div>
      <div className="field">
        <label htmlFor={ids[1]}>{t("ui.password.next")}</label>
        <input
          id={ids[1]}
          type="password"
          autoComplete="new-password"
          value={next}
          onChange={(e) => setNext(e.target.value)}
          required
          minLength={MIN_PASSWORD_LENGTH}
        />
        <small className="muted">{t("ui.password.hint", { min: MIN_PASSWORD_LENGTH })}</small>
      </div>
      <div className="field">
        <label htmlFor={ids[2]}>{t("ui.password.repeat")}</label>
        <input
          id={ids[2]}
          type="password"
          autoComplete="new-password"
          value={repeat}
          onChange={(e) => setRepeat(e.target.value)}
          required
        />
      </div>
      {error ? (
        <p role="alert" className="note-error">
          {error}
        </p>
      ) : null}
      <div className="row">
        <button type="submit" className="btn btn-primary" disabled={busy}>
          {busy ? t("ui.password.working") : t("ui.password.submit")}
        </button>
        {props.onSignOut ? (
          <button type="button" className="btn btn-quiet" onClick={props.onSignOut}>
            {t("ui.password.signOut")}
          </button>
        ) : null}
      </div>
    </form>
  );
}
